import React, { useEffect, useMemo, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { apiGet, apiPost, formatLKR } from "@/lib/api";
import { toast } from "sonner";
import { Info, ChevronsUpDown, Check } from "lucide-react";
import axios from "axios";

const HoldingDialog = ({ open, onClose, onSaved, portfolioId, editing, prefill }) => {
  const [securities, setSecurities] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [companyCode, setCompanyCode] = useState("");
  const [companyOpen, setCompanyOpen] = useState(false);
  const [shareType, setShareType] = useState("voting");
  const [shares, setShares] = useState("");
  const [price, setPrice] = useState("");
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [notes, setNotes] = useState("");
  const [latestPrice, setLatestPrice] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    apiGet("/securities").then(setSecurities).catch(() => {});
    apiGet("/companies").then(setCompanies).catch(() => {});
  }, []);

  // Load editing data
  useEffect(() => {
    if (editing) {
      setCompanyCode(editing.company_code);
      setShareType(editing.share_type);
      setShares(editing.shares_purchased);
      setPrice(editing.purchase_price);
      setDate(new Date(editing.purchase_date).toISOString().slice(0, 10));
      setNotes(editing.notes || "");
    } else if (prefill && open) {
      setCompanyCode(prefill.company_code || "");
      setShareType(prefill.share_type || "voting");
      setShares(prefill.shares_purchased ?? "");
      // Phase 9 Fix 1 — Pre-fill purchase price with the suggested close
      // price. User can override with their actual broker fill price.
      setPrice(prefill.purchase_price ?? "");
      setDate(new Date().toISOString().slice(0, 10));
      setNotes(prefill._from_suggestion ? "From AI suggestion" : "");
      // Surface the latest price banner for visual confirmation
      if (prefill.purchase_price != null) {
        setLatestPrice(prefill.purchase_price);
      }
    } else if (open) {
      setCompanyCode("");
      setShareType("voting");
      setShares("");
      setPrice("");
      setDate(new Date().toISOString().slice(0, 10));
      setNotes("");
      setLatestPrice(null);
    }
  }, [editing, prefill, open]);

  const groupedByCompany = useMemo(() => {
    const map = {};
    for (const s of securities) {
      if (!map[s.company_code]) map[s.company_code] = { company_code: s.company_code, display_name: s.display_name, types: [] };
      map[s.company_code].types.push(s);
    }
    return Object.values(map).sort((a, b) => a.company_code.localeCompare(b.company_code));
  }, [securities]);

  // Group companies by sector for the searchable Combobox dropdown.
  const sectorGroups = useMemo(() => {
    const sectorByCode = Object.fromEntries(
      (companies || []).map((c) => [c.company_code, c.sector || "Other"]),
    );
    const groups = {};
    for (const c of groupedByCompany) {
      const sector = sectorByCode[c.company_code] || "Other";
      if (!groups[sector]) groups[sector] = [];
      groups[sector].push(c);
    }
    return Object.entries(groups)
      .map(([sector, items]) => ({ sector, items: items.sort((a, b) => a.company_code.localeCompare(b.company_code)) }))
      .sort((a, b) => a.sector.localeCompare(b.sector));
  }, [companies, groupedByCompany]);

  const selectedCompany = groupedByCompany.find((c) => c.company_code === companyCode);
  const dualClass = selectedCompany && selectedCompany.types.length > 1;
  const cseSymbol = useMemo(() => {
    if (!selectedCompany) return "";
    if (dualClass) {
      const m = selectedCompany.types.find((t) => t.share_type === shareType);
      return m?.cse_symbol || selectedCompany.types[0].cse_symbol;
    }
    return selectedCompany.types[0].cse_symbol;
  }, [selectedCompany, shareType, dualClass]);

  // Fetch latest price for prefill
  useEffect(() => {
    if (!cseSymbol) { setLatestPrice(null); return; }
    apiGet(`/prices/${cseSymbol}?days=1`).then((d) => {
      if (d && d.length) setLatestPrice(d[d.length - 1].close_price);
      else setLatestPrice(null);
    }).catch(() => setLatestPrice(null));
  }, [cseSymbol]);

  const submit = async () => {
    if (!cseSymbol) { toast.error("Pick a company"); return; }
    if (!shares || Number(shares) <= 0) { toast.error("Shares must be positive"); return; }
    if (!price || Number(price) <= 0) { toast.error("Purchase price must be positive"); return; }
    setSubmitting(true);
    try {
      const body = {
        cse_symbol: cseSymbol,
        shares_purchased: Number(shares),
        purchase_price: Number(price),
        purchase_date: date,
        notes,
      };
      if (editing) {
        await axios.put(`${process.env.REACT_APP_BACKEND_URL}/api/holdings/${editing.holding_id}`, body);
        toast.success("Holding updated");
      } else {
        await apiPost(`/portfolios/${portfolioId}/holdings`, body);
        toast.success("Holding added");
      }
      onSaved();
    } catch (e) {
      toast.error("Save failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        className="sm:max-w-lg"
        data-testid="holding-dialog"
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle>{editing ? "Edit holding" : "Add new holding"}</DialogTitle>
        </DialogHeader>

        {/* Phase 9 Fix 1 — When pre-filled from an AI suggestion show a clear
            confirmation banner with company, ticker, suggested shares, and
            suggested price so the user knows the dialog is auto-populated. */}
        {prefill?._from_suggestion && (
          <div
            className="rounded-md border border-primary/30 bg-primary/5 p-3 text-xs space-y-1"
            data-testid="holding-prefill-banner"
          >
            <div className="font-semibold text-foreground flex items-center gap-1.5">
              <Info className="h-3.5 w-3.5 text-primary" /> Pre-filled from AI suggestion
            </div>
            <div className="grid grid-cols-2 gap-x-4 gap-y-0.5 text-muted-foreground">
              {prefill.company_name && (
                <div>
                  <span className="text-foreground font-medium">Company: </span>
                  {prefill.company_name}
                </div>
              )}
              {prefill.cse_symbol && (
                <div>
                  <span className="text-foreground font-medium">Ticker: </span>
                  <span className="font-mono">{prefill.cse_symbol}</span>
                </div>
              )}
              {prefill.purchase_price != null && (
                <div>
                  <span className="text-foreground font-medium">Suggested price: </span>
                  {formatLKR(prefill.purchase_price)}
                </div>
              )}
              {prefill.shares_purchased != null && (
                <div>
                  <span className="text-foreground font-medium">Suggested shares: </span>
                  {prefill.shares_purchased}
                </div>
              )}
            </div>
            <div className="text-[10px] text-muted-foreground italic pt-1">
              Adjust price below if your broker fill price was different.
            </div>
          </div>
        )}

        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label>Company</Label>
            <Popover open={companyOpen} onOpenChange={setCompanyOpen}>
              <PopoverTrigger asChild>
                <Button
                  type="button"
                  variant="outline"
                  role="combobox"
                  aria-expanded={companyOpen}
                  className="w-full justify-between font-normal"
                  data-testid="holding-company-select"
                >
                  {selectedCompany ? (
                    <span className="truncate">
                      <span className="font-mono text-foreground">{selectedCompany.company_code}</span>
                      <span className="text-muted-foreground"> — {selectedCompany.display_name?.replace(/\s\(.*\)$/, "") || selectedCompany.company_code}</span>
                    </span>
                  ) : prefill?.company_name ? (
                    /* Phase 9 Fix 1 — Show prefill company name even before
                       /securities API has loaded so the dialog never looks empty. */
                    <span className="truncate">
                      <span className="font-mono text-foreground">{prefill.company_code}</span>
                      <span className="text-muted-foreground"> — {prefill.company_name}</span>
                    </span>
                  ) : (
                    <span className="text-muted-foreground">Select a company…</span>
                  )}
                  <ChevronsUpDown className="h-4 w-4 opacity-50 shrink-0" />
                </Button>
              </PopoverTrigger>
              <PopoverContent
                className="p-0 w-[var(--radix-popover-trigger-width)] max-h-80"
                align="start"
                data-testid="holding-company-popover"
              >
                <Command
                  filter={(value, search) => {
                    const v = value.toLowerCase();
                    const s = search.toLowerCase();
                    return v.includes(s) ? 1 : 0;
                  }}
                >
                  <CommandInput placeholder="Search by code, name or sector…" />
                  <CommandList className="max-h-72">
                    <CommandEmpty>No company found.</CommandEmpty>
                    {sectorGroups.map((g) => (
                      <CommandGroup
                        key={g.sector}
                        heading={`${g.sector} (${g.items.length})`}
                      >
                        {g.items.map((c) => {
                          const label = c.display_name?.replace(/\s\(.*\)$/, "") || c.company_code;
                          const value = `${c.company_code} ${label} ${g.sector}`;
                          return (
                            <CommandItem
                              key={c.company_code}
                              value={value}
                              onSelect={() => {
                                setCompanyCode(c.company_code);
                                setShareType("voting");
                                setCompanyOpen(false);
                              }}
                              data-testid={`holding-company-option-${c.company_code}`}
                            >
                              <Check
                                className={`mr-2 h-3.5 w-3.5 ${companyCode === c.company_code ? "opacity-100" : "opacity-0"}`}
                              />
                              <span className="font-mono text-xs w-12 shrink-0">{c.company_code}</span>
                              <span className="text-sm truncate">{label}</span>
                            </CommandItem>
                          );
                        })}
                      </CommandGroup>
                    ))}
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
          </div>

          {dualClass && (
            <Alert data-testid="holding-share-type-prompt">
              <Info className="h-4 w-4" />
              <AlertDescription className="flex items-center justify-between gap-3 flex-wrap">
                <span className="text-sm">This company has both share classes — which one did you buy?</span>
                <ToggleGroup type="single" value={shareType} onValueChange={(v) => v && setShareType(v)}>
                  <ToggleGroupItem value="voting" data-testid="holding-sharetype-voting">Voting (.N0000)</ToggleGroupItem>
                  <ToggleGroupItem value="non_voting" data-testid="holding-sharetype-nonvoting">Non-voting (.X0000)</ToggleGroupItem>
                </ToggleGroup>
              </AlertDescription>
            </Alert>
          )}

          {cseSymbol && (
            <div className="text-xs text-muted-foreground">
              Selected ticker: <span className="font-mono text-foreground">{cseSymbol}</span>
              {latestPrice !== null && (
                <>
                  {" · "}Latest close: <span className="tabular text-foreground">{formatLKR(latestPrice)}</span>
                  <button
                    type="button"
                    onClick={() => setPrice(latestPrice)}
                    className="ml-2 text-primary hover:underline"
                  >
                    use
                  </button>
                </>
              )}
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Shares purchased</Label>
              <Input type="number" min="0" step="1" value={shares} onChange={(e) => setShares(e.target.value)} data-testid="holding-shares-input" />
            </div>
            <div className="space-y-1.5">
              <Label>Purchase price (LKR / share)</Label>
              <Input type="number" min="0" step="0.01" value={price} onChange={(e) => setPrice(e.target.value)} data-testid="holding-price-input" />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label>Purchase date</Label>
            <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="holding-date-input" />
          </div>

          <div className="space-y-1.5">
            <Label>Notes (optional)</Label>
            <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} data-testid="holding-notes-input" />
          </div>

          {shares && price && (
            <BrokeragePreview shares={shares} price={price} />
          )}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={submitting} data-testid="holding-save-button">
            {submitting ? "Saving…" : editing ? "Save changes" : "Add holding"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default HoldingDialog;

// ----------------------------------------------------------------------
// Stage 1 — Inline brokerage preview shown beneath the price input.
// Computes:  gross → brokerage → total cost → effective per share →
//            breakeven price (incl. selling brokerage).
// Falls back gracefully if the API is unreachable.
// ----------------------------------------------------------------------
const BrokeragePreview = ({ shares, price }) => {
  const [calc, setCalc] = useState(null);
  const [rates, setRates] = useState({ buy_rate: 0.0121, sell_rate: 0.0121 });

  useEffect(() => {
    apiGet("/portfolio-settings").then(setRates).catch(() => {});
  }, []);

  useEffect(() => {
    const s = Number(shares);
    const p = Number(price);
    if (!s || !p || s <= 0 || p <= 0) {
      setCalc(null);
      return;
    }
    let cancelled = false;
    apiPost("/portfolio-settings/preview-buy", { shares: s, price: p })
      .then((r) => { if (!cancelled) setCalc(r); })
      .catch(() => {
        // Local fallback — shouldn't happen but safer than blank UI
        const gross = s * p;
        const brokerage = gross * rates.buy_rate;
        const total = gross + brokerage;
        if (!cancelled) {
          setCalc({
            gross_amount: gross,
            brokerage_buy: brokerage,
            total_cost: total,
            effective_per_share: total / s,
            breakeven_price: total / (s * (1 - rates.sell_rate)),
            breakeven_gain_pct: ((total / (s * (1 - rates.sell_rate))) / p - 1) * 100,
          });
        }
      });
    return () => { cancelled = true; };
  }, [shares, price, rates.buy_rate, rates.sell_rate]);

  if (!calc) return null;
  const ratePct = (rates.buy_rate * 100).toFixed(2);
  return (
    <div
      data-testid="holding-brokerage-preview"
      className="rounded-md border border-border bg-muted/30 p-3 text-sm space-y-1.5"
    >
      <div className="flex items-center justify-between">
        <span className="text-muted-foreground">Purchase amount</span>
        <span className="tabular font-medium">{formatLKR(calc.gross_amount)}</span>
      </div>
      <div className="flex items-center justify-between">
        <span className="text-muted-foreground">Brokerage ({ratePct}%)</span>
        <span className="tabular font-medium">{formatLKR(calc.brokerage_buy)}</span>
      </div>
      <div className="flex items-center justify-between border-t border-border pt-1.5">
        <span className="font-medium">Total cost</span>
        <span className="tabular font-semibold">{formatLKR(calc.total_cost)}</span>
      </div>
      <div className="flex items-center justify-between text-xs text-muted-foreground">
        <span>Effective cost / share</span>
        <span className="tabular">{formatLKR(calc.effective_per_share, { decimals: 2 })}</span>
      </div>
      <div className="flex items-center justify-between text-xs text-muted-foreground">
        <span>Breakeven price (incl. sell brokerage)</span>
        <span className="tabular">
          {formatLKR(calc.breakeven_price, { decimals: 2 })}
          {" "}
          <span className="text-[10px]">(+{(calc.breakeven_gain_pct || 0).toFixed(2)}%)</span>
        </span>
      </div>
    </div>
  );
};
